import { jsx, jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { styled } from "@mui/system";
import Button from "../Button";
import { ArrowLeftIcon, CloseIcon } from "@dion-web/ui-icons";
import Typography from "@mui/material/Typography";
import { getMultilineEllipsisSX } from "../../shared/utils/styles";
import isFunction from "lodash/isFunction";
import Box from "@mui/material/Box";
import { DION_SCROLLBAR } from "../../theme";
import { useCurrentColor } from "../../shared";
import Divider from "@mui/material/Divider";
import { nonEmptyItem } from "../../shared/helpers";
import isEmpty from "lodash/isEmpty";
const MAX_CONTROLS = 3;
const TITLE_MAX_LINES = 3;
const StyledDrawer = styled(Drawer)(({ theme }) => {
  return {
    "& .MuiPaper-root": {
      width: "480px",
      maxWidth: "max(480px, 50%)",
      borderLeft: `1px solid ${theme.palette.lines.divider}`
    }
  };
});
const StyledHeaderBox = styled(Box)(({ theme }) => ({
  margin: `${theme.sidePanel.spacing.titleMargin}px`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center"
}));
const StyledHeaderTypography = styled(Typography)(({}) => ({
  maxWidth: "100%",
  wordWrap: "break-word",
  padding: "0 8px",
  ...getMultilineEllipsisSX(TITLE_MAX_LINES),
  maxHeight: `${TITLE_MAX_LINES}lh`,
  // ?
  flexGrow: 1
}));
const StyledHeaderButton = styled(Button)`
  align-self: flex-start;
`;
const StyledContentContainerBox = styled(Box)(({ theme }) => ({
  wordWrap: "break-word",
  // overflow: 'auto',
  overflowY: "scroll",
  overflowX: "hidden",
  ...DION_SCROLLBAR,
  paddingRight: `${theme.scroll.spacing.sidePadding}px`,
  marginRight: `${theme.sidePanel.spacing.margin - theme.scroll.size.m - theme.scroll.spacing.sidePadding}px`,
  marginLeft: `${theme.sidePanel.spacing.margin}px`,
  minHeight: "24px",
  height: "100%"
}));
const StyledActionsBox = styled(Box)(
  ({ theme }) => {
    return {
      display: "flex",
      alignItems: "center",
      gap: `${theme.buttonGroup.spacing.m}px`,
      padding: `${theme.sidePanel.spacing.actionsMargin}px`,
      boxSize: "border-box",
      overflow: "hidden",
      justifyContent: "flex-start",
      flexDirection: "row-reverse"
    };
  }
);
const StyledDivider = styled(Divider)(({ theme }) => ({
  minWidth: "100%",
  height: "1px"
}));
const StyledTopDivider = styled(StyledDivider)(() => ({
  "&::after": {
    content: '""',
    position: "absolute",
    height: "4px",
    width: "100%",
    background: "linear-gradient(color-mix(in srgb, black 10%, transparent), transparent)",
    zIndex: 20,
    marginTop: "1px"
  }
}));
const StyledBottomDivider = styled(StyledDivider)(() => ({
  "&::before": {
    content: '""',
    position: "absolute",
    height: "2px",
    width: "100%",
    background: "linear-gradient(transparent, color-mix(in srgb, black 10%, transparent))",
    marginTop: "-2px",
    zIndex: 20
  }
}));
const SidePanel = (props) => {
  const hasBackButton = isFunction(props.onBackClick);
  const [isScrollbarVisible, setScrollVisible] = useState(false);
  const [contentRef, setContentRef] = useState();
  const { ctx, Provider } = useCurrentColor("elevationObject", "contrast", true);
  const allControls = React.useMemo(
    () => {
      return (props.controls || []).filter(nonEmptyItem).slice(0, props.maxControls ?? MAX_CONTROLS);
    },
    [props.controls, props.maxControls]
  );
  const hasControls = !isEmpty(allControls);
  useEffect(() => {
    const scrollbarVisible = contentRef && contentRef?.scrollHeight > contentRef?.clientHeight || false;
    if (isScrollbarVisible !== scrollbarVisible)
      setScrollVisible(scrollbarVisible);
  });
  return /* @__PURE__ */ jsx(Provider, { value: ctx, children: /* @__PURE__ */ jsxs(StyledDrawer, { ...props, anchor: "right", hideBackdrop: true, title: void 0, children: [
    /* @__PURE__ */ jsxs(StyledHeaderBox, { children: [
      hasBackButton && /* @__PURE__ */ jsx(
        StyledHeaderButton,
        {
          onClick: props.onBackClick,
          variant: "ghost",
          startIcon: /* @__PURE__ */ jsx(ArrowLeftIcon, {}),
          size: "m"
        }
      ),
      props.title && /* @__PURE__ */ jsx(StyledHeaderTypography, { variant: "h6", children: props.title }),
      /* @__PURE__ */ jsx(
        StyledHeaderButton,
        {
          onClick: props.onClose,
          variant: "ghost",
          startIcon: /* @__PURE__ */ jsx(CloseIcon, {}),
          size: "m"
        }
      )
    ] }),
    isScrollbarVisible && props.shadeContent ? /* @__PURE__ */ jsx(StyledTopDivider, {}) : /* @__PURE__ */ jsx(StyledDivider, {}),
    props.children && /* @__PURE__ */ jsx(StyledContentContainerBox, { ref: setContentRef, children: props.children }),
    isScrollbarVisible && props.shadeContent ? /* @__PURE__ */ jsx(StyledBottomDivider, {}) : /* @__PURE__ */ jsx(StyledDivider, {}),
    hasControls && /* @__PURE__ */ jsx(StyledActionsBox, { children: allControls })
  ] }) });
};
export {
  SidePanel
};
